import { z } from 'zod';
export const sessionModeSchema = z.enum(['guided', 'expert']);
export const provenanceSourceSchema = z.enum([
    'user',
    'default',
    'calculated',
    'scenario',
    'market_data',
    'inferred',
]);
export const defaultStatusSchema = z
    .enum(['accepted', 'confirmed', 'overridden'])
    .nullable();
export const provenanceRecordSchema = z.object({
    inputName: z.string(),
    value: z.any(),
    source: provenanceSourceSchema,
    confidence: z.number().min(0).max(1),
    timestamp: z.string(),
    messageId: z.string().optional(),
    reasoning: z.string().optional(),
    previousValue: z.any().optional(),
    previousSource: provenanceSourceSchema.optional(),
    sourceDetail: z.string().optional(),
    sourceUrl: z.string().optional(),
    dataAsOf: z.string().optional(),
    defaultOffered: z.any().optional(),
    defaultStatus: defaultStatusSchema.optional(),
    /**
     * Name of the provenance record this one was derived from. Used when an
     * input is populated by a transformation of another input (e.g.,
     * `SeniorInterestRate` derived from the market-data `InterestRate`).
     * Readers (V3 export, text formatter) can group derived records under
     * their parent so the provenance report doesn't double-count.
     */
    derivedFrom: z.string().optional(),
});
export const provenanceExportSchema = z.object({
    modelId: z.string(),
    conversationId: z.string(),
    generatedAt: z.string(),
    inputs: z.array(z.object({
        name: z.string(),
        displayName: z.string(),
        value: z.any(),
        source: provenanceSourceSchema,
        confidence: z.number(),
        timestamp: z.string(),
        messageId: z.string().optional(),
        reasoning: z.string().optional(),
    })),
    summary: z.object({
        total: z.number(),
        userProvided: z.number(),
        defaults: z.number(),
        averageConfidence: z.number(),
    }),
});
/**
 * An input that was set under a previous schema version but doesn't exist in
 * the current pinned model_version. Preserved on fork across schema drift so
 * data isn't silently lost; surfaced to the user via the assumption summary
 * panel + fork toast.
 */
export const orphanedInputSchema = z.object({
    value: z.any(),
    source: provenanceSourceSchema,
    originalSchemaVersion: z.string(),
});
/**
 * Per-metric history entry for the sparkline ring buffer on
 * `agent_state.metricHistory`. Each `recordEdit()` writeback (Phase 1
 * Session 4) appends a new entry per whitelisted KPI metric. Bounded ring
 * buffer (last 32 entries per metric) to prevent unbounded JSONB growth.
 *
 * `value` is the engine-normalized output value (post-`applyOpsToEngine`)
 * for the metric at the moment the edit committed. `capturedAt` is the
 * server-side ISO timestamp of the writeback.
 *
 * Lazy-populate: existing conversations get an empty `metricHistory: {}`
 * default at first post-deploy `recordEdit`. Sparklines render gracefully
 * on conversations with <2 history entries (single dot OR "No history yet"
 * placeholder; Phase 3 wiring).
 */
export const metricHistoryEntrySchema = z.object({
    value: z.number(),
    capturedAt: z.string().datetime(),
});
export const conversationAgentStateSchema = z.object({
    modelId: z.string(),
    modelVersion: z.string(),
    sessionMode: sessionModeSchema,
    inputProvenance: z.record(z.string(), provenanceRecordSchema).default({}),
    orphanedInputs: z.record(z.string(), orphanedInputSchema).default({}),
    generatedExcelUrl: z.string().optional(),
    generatedProvenanceUrl: z.string().optional(),
    sensitivityResults: z.array(z.any()).default([]),
    scenarioHistory: z.array(z.any()).default([]),
    /**
     * Per-metric history ring buffer. Keyed by metric name (e.g. 'IRR',
     * 'EquityMultiple'). Capped at 32 entries per metric — `recordEdit`
     * writeback (Phase 1 Session 4) trims older entries on append. Defaults
     * to `{}` so legacy `agent_state` rows without the field parse cleanly.
     */
    metricHistory: z
        .record(z.string(), z.array(metricHistoryEntrySchema))
        .default({}),
    // NEW — cockpit-lovable-alignment Phase 2. Creator-intent metadata captured
    // at conversation-create time via <NewDealModal> (cockpit-lovable-alignment
    // Phase 4 consumer); persisted by ConversationServiceV2.create (Phase 2
    // Session 2.2) and projected onto the list-endpoint response via JSONB
    // ->> extraction (Phase 2 Session 2.3). Preserved across tool runs by the
    // streamMessage post-stream merge (Phase 2 Session 2.4) — 4th preserved
    // key per root CLAUDE.md footgun #6 alongside inputProvenance +
    // metricHistory (coordinates field removed in P4 S4.11; coords now in
    // inputProvenance via ADR-0013). The field is OPTIONAL +
    // NULLABLE so legacy state without it parses cleanly. Storage uses
    // request-side names targetIrr/targetYoc; response-side aliasing happens
    // at projection time (research/phase-2.md § 11 R1).
    //
    // conversation-model-versioning F11 fix (2026-05-18, /complete Step 4g batch
    // sweep): the location feature (ADR 0003/0004, Phase 4/6) made
    // `dealMetadata[<locationInputKey>] = PropertyResolution` a LOAD-BEARING
    // contract — `isAddressedLocation` (server completeness-gate.service.ts:149
    // AND the web version-completeness mirror) reads `dealMetadata[key] != null`
    // to decide a location input is addressed. But this schema was a CLOSED
    // `z.object` so `parseAgentState` (used by `get` AND `checkVersionCompleteness`)
    // STRIPPED that payload → location always "not set", version-create 422.
    // Worse: the writeback sets `market = metro ?? cbsa ?? null` and a no-CBSA
    // address (e.g. a DC/rural address) yields `null`, which `z.string().optional()`
    // REJECTS → the WHOLE agent_state failed safeParse → `get` fell back to an
    // empty state (VariablesPanel 0/43, "Property location — not set", persists
    // across reload). Fix: `market` is `.nullable()` (a no-CBSA resolution is
    // legitimate) + `.passthrough()` preserves the per-location-input resolution
    // payload (matches the codebase `.passthrough()` convention in
    // billing.types.ts). Both changes are additive/back-compat: known-key
    // destructurers are unaffected; `market` widens string→string|null and every
    // consumer (`conversationToDeal` `?? undefined`, JSONB `->>`) is already
    // null-tolerant.
    dealMetadata: z
        .object({
        market: z.string().nullable().optional(),
        units: z.number().optional(),
        basis: z.string().optional(),
        targetIrr: z.string().optional(),
        targetYoc: z.string().optional(),
    })
        .passthrough()
        .nullable()
        .optional(),
    /**
     * Marks `inputValues` as already in HF-canonical form (percentages as
     * decimal fractions, etc.) so `ComputationEngine.fromSavedInputs` skips
     * re-normalization on load. Stamped `true` by `buildAgentState` on
     * every persist; legacy rows without the field parse as `false` and get
     * one normalize-then-canonicalize round on first reload (the cascading
     * drift in `normalizePercentage` stops after that). Model-agnostic — no
     * specific input names are hardcoded. (`external-sharing` follow-up,
     * 2026-05-10.)
     */
    valuesCanonical: z.boolean().default(false),
});
// --- Provenance Export V2 (3-section structured report) ---
export const provenanceExportInputSchema = z.object({
    name: z.string(),
    displayName: z.string(),
    value: z.any(),
    source: provenanceSourceSchema,
    confidence: z.number(),
    timestamp: z.string(),
    messageId: z.string().optional(),
    reasoning: z.string().optional(),
    sourceDetail: z.string().optional(),
    sourceUrl: z.string().optional(),
    dataAsOf: z.string().optional(),
    defaultOffered: z.any().optional(),
    defaultStatus: defaultStatusSchema.optional(),
    benchmarkRange: z
        .object({
        min: z.number(),
        max: z.number(),
        source: z.string(),
    })
        .optional(),
    benchmarkStatus: z.enum(['within', 'above', 'below']).optional(),
    validationWarning: z.string().optional(),
    /** Mirrors `ProvenanceRecord.derivedFrom` so export readers can group derived inputs under their parent. */
    derivedFrom: z.string().optional(),
});
export const provenanceExportV2Schema = z.object({
    modelId: z.string(),
    conversationId: z.string(),
    arcId: z.string().optional(),
    dealType: z.string().optional(),
    market: z.string().optional(),
    generatedAt: z.string(),
    userAssumptions: z.array(provenanceExportInputSchema),
    arcanumAssumptions: z.array(provenanceExportInputSchema),
    derived: z.array(provenanceExportInputSchema),
    summary: z.object({
        total: z.number(),
        userProvided: z.number(),
        arcanumDefaults: z.number(),
        derived: z.number(),
        averageConfidence: z.number(),
    }),
});
// --- Provenance Export V3 (4-section: user, market, arcanum, derived) ---
export const provenanceExportV3Schema = z.object({
    modelId: z.string(),
    conversationId: z.string(),
    conversationTitle: z.string().optional(),
    arcId: z.string().optional(),
    dealType: z.string().optional(),
    market: z.string().optional(),
    generatedAt: z.string(),
    userAssumptions: z.array(provenanceExportInputSchema),
    marketDefaults: z.array(provenanceExportInputSchema),
    arcanumAssumptions: z.array(provenanceExportInputSchema),
    derived: z.array(provenanceExportInputSchema),
    summary: z.object({
        total: z.number(),
        userProvided: z.number(),
        marketDefaults: z.number(),
        arcanumDefaults: z.number(),
        derived: z.number(),
        averageConfidence: z.number(),
    }),
});
